import type { ApiError } from "../services/api/apiClient";


export type ErrorKind = "toast" | "toast_warning" | "reconnect_account" | "modal";

export interface TranslatedError {
	message: string;
	kind: ErrorKind;
	code?: string;
}

const KNOWN: Record<string, { msg: string; kind: ErrorKind }> = {
	// --- Auth / sesión ---
	CSRF_FAILED: {
		msg: "Tu sesión de seguridad expiró. Recarga la página e inténtalo de nuevo.",
		kind: "toast",
	},
	SESSION_NOT_ACTIVE: {
		msg: "La sesión de WhatsApp se cayó. Reconecta la cuenta para seguir enviando.",
		kind: "reconnect_account",
	},
	TOKEN_EXPIRED: {
		msg: "Tu sesión expiró. Vuelve a iniciar sesión.",
		kind: "toast",
	},
	TOKEN_INVALID: {
		msg: "Sesión inválida. Vuelve a iniciar sesión.",
		kind: "toast",
	},
	INVALID_CREDENTIALS: {
		msg: "Correo electrónico o contraseña incorrectos.",
		kind: "toast",
	},
	NOT_AUTHENTICATED: {
		msg: "Necesitas iniciar sesión para continuar.",
		kind: "toast",
	},
	FORBIDDEN: {
		msg: "No tienes permiso para realizar esta acción.",
		kind: "toast",
	},

	// --- Rate limits / quota ---
	RATE_LIMITED: {
		msg: "Demasiadas solicitudes en poco tiempo. Espera un momento e intenta de nuevo.",
		kind: "toast_warning",
	},
	USAGE_LIMIT_EXCEEDED: {
		msg: "Has alcanzado el límite de tu plan actual.",
		kind: "toast_warning",
	},

	// --- Validación / Operaciones ---
	VALIDATION_FAILED: {
		msg: "Los datos enviados no son válidos.",
		kind: "toast",
	},
	EDIT_FAILED: {
		msg: "No se pudo editar el mensaje en WhatsApp.",
		kind: "toast",
	},
	EDIT_TIME_EXPIRED: {
		msg: "El tiempo para editar este mensaje en WhatsApp ha expirado (límite de 15 minutos).",
		kind: "toast_warning",
	},
};

export function translateApiError(err: unknown): TranslatedError {
	const apiErr = err as Partial<ApiError> | undefined;
	const code = apiErr?.code;
	const status = apiErr?.status ?? 0;

	if (code && KNOWN[code]) {
		return { message: KNOWN[code].msg, kind: KNOWN[code].kind, code };
	}

	// Fallback by HTTP status when the backend didn't send a code we know.
	if (status === 401) {
		return {
			message: "Tu sesión expiró. Vuelve a iniciar sesión.",
			kind: "toast",
			code: "TOKEN_EXPIRED",
		};
	}
	if (status === 403) {
		return {
			message: "No tienes permiso para realizar esta acción.",
			kind: "toast",
			code: "FORBIDDEN",
		};
	}
	if (status === 404) {
		return {
			message: "El recurso solicitado no existe.",
			kind: "toast",
		};
	}
	if (status === 412) {
		// Precondition Failed — typically SESSION_NOT_ACTIVE for this app.
		return {
			message:
				apiErr?.message ||
				"La operación no se puede completar en el estado actual.",
			kind: "reconnect_account",
		};
	}
	if (status === 429) {
		return {
			message: "Demasiadas solicitudes. Espera un momento.",
			kind: "toast_warning",
		};
	}
	if (status >= 500) {
		return {
			message: "Error del servidor. Intenta de nuevo en un momento.",
			kind: "toast",
		};
	}
	if (status === 0) {
		return {
			message: "Sin conexión con el servidor. Revisa tu internet.",
			kind: "toast",
		};
	}

	// Last resort: backend message (which may be in English — better than
	// showing nothing) but never echo raw detail with no translation.
	return {
		message: apiErr?.message || "Algo salió mal. Intenta de nuevo.",
		kind: "toast",
	};
}
